import PropTypes from 'prop-types'

import {
  actionTypes,
  authTypes,
  transitions,
  DEBOUNCE_TIME,
} from '@adalo/constants'

import { getEndpoint, getEndpointMethod } from './apis'
import { apiURL } from './urls'
import { Alert } from './alerts'
import { getId } from './sources'
import { mapObject, toDeep, deepGet } from './objects'
import { getSingleActionDependencies, getInputs } from './dependencies'
import { share } from './sharing'
import { request } from './networking'
import { getInputIds } from './inputs'
import { openLink } from './linking'
import { sleep } from './sleep'
import { addRelatedBindings } from './bindings'
import { logEvent } from './analytics'
import { executeAPICustomAction } from './custom-action'
import { evaluateCondition } from './conditions'
import { isExternalCollection } from './externalCollections'

import {
  registerDevice,
  unregisterDevice,
  sendNotification,
} from './notifications'

import { requestPermission } from './notification-wrapper'

import { changeValue, resetValue } from '../ducks/formInputs'

import {
  setAuthToken,
  unsafeGetToken,
  authenticate,
  authenticateExternal,
  logout,
} from '../ducks/auth'

import {
  createObject,
  updateObject,
  deleteObject,
  createAssociation,
  deleteAssociation,
} from '../ducks/data'

import { TARGET_FORGOT_PASSWORD } from './forgotPassword'
import { disableTrialFeature } from './trial'

export const actionContextTypes = {
  navigate: PropTypes.func,
  getBinding: PropTypes.func,
  getBindings: PropTypes.func,
  getBindingsList: PropTypes.func,
  getParams: PropTypes.func,
  getAssetURL: PropTypes.func,
  getBaseURL: PropTypes.func,
  getBaseAPIURL: PropTypes.func,
  getDatasources: PropTypes.func,
  getFileUploadsBaseURL: PropTypes.func,
  getImageUploadsBaseURL: PropTypes.func,
  setDirty: PropTypes.func,
  getApp: PropTypes.func,
  getStore: PropTypes.func,
  getNotificationsURL: PropTypes.func,
  getDeviceId: PropTypes.func,
  isPreviewer: PropTypes.bool,
  getComponent: PropTypes.func,
  appVersion: PropTypes.string,
  disableAnalytics: PropTypes.bool,
}

const handleConditionalAction = (conditional, deps) => {
  let {
    conditional: value,
    conditionalComparison,
    conditionalComparison2,
  } = deps

  value = value && value.value
  let comparison = conditionalComparison && conditionalComparison.value
  let comparison2 = conditionalComparison2 && conditionalComparison2.value

  return evaluateCondition(
    value,
    conditional.comparator,
    conditional.source.dataType,
    comparison,
    comparison2
  )
}

export const executeAction = async (action, helpers, dependencies = {}) => {
  let {
    dispatch,
    navigate,
    getApp,
    getBinding,
    getBindings,
    getParams,
    getBaseURL,
    getStore,
    getComponent,
    getDatasources,
  } = helpers

  dispatch = dispatch || getStore().dispatch

  let createInputs = []

  let { actions } = action

  let createdObjects = {}
  let customActionOutputs = {}

  let selectorOpts = {
    getParams,
    createdObjects,
    getBinding,
  }

  let getCreatedObject = (datasourceId, tableId) => {
    return createdObjects[`${datasourceId}.${tableId}`]
  }

  let getCustomActionOutput = (customActionId, key) =>
    deepGet(customActionOutputs[customActionId], key.split('.'))

  helpers = {
    ...helpers,
    createdObjects,
    getCreatedObject,
    customActionOutputs,
    getCustomActionOutput,
  }

  let disabledActionList = []
  let potentialDisabledActionsList = []

  for (let i = 0; i < actions.length; i += 1) {
    let { actionType, options } = actions[i]
    if (actionType === actionTypes.CUSTOM) {
      potentialDisabledActionsList.push(i)
    } else if (options.tableId && options.datasourceId) {
      let { tableId, datasourceId } = options
      let datasource = getDatasources()[datasourceId]
      if (isExternalCollection({ datasource, tableId })) {
        potentialDisabledActionsList.push(i)
      }
    }
  }
  if (potentialDisabledActionsList.length !== 0) {
    const app = getApp()

    if (disableTrialFeature(app)) {
      disabledActionList = potentialDisabledActionsList
    }
  }

  try {
    for (let i = 0; i < actions.length; i += 1) {
      if (disabledActionList.includes(i)) continue

      let { id, actionType, options } = actions[i]

      let sleepTime = 0

      const formInputs = getInputs(actions[i], helpers)
      if (formInputs && formInputs.length !== 0) {
        sleepTime = DEBOUNCE_TIME
      }

      await sleep(sleepTime)
      let state = getStore().getState()

      let deps
      let inputs = []

      if (dependencies[id]) {
        deps = dependencies[id]
      } else {
        let spread = getSingleActionDependencies(state, actions[i], helpers)
        deps = spread[0]
        inputs = spread[1]
      }

      if (options.conditional) {
        let result = handleConditionalAction(options.conditional, deps)
        if (!result) continue
      }

      if (actionType === actionTypes.CREATE_OBJECT) {
        createInputs = createInputs.concat(inputs, getInputIds(deps))
        let obj = await executeCreate(dispatch, options, deps, helpers)
        let { datasourceId, tableId } = options
        createdObjects[`${datasourceId}.${tableId}`] = obj.id
      } else if (actionType === actionTypes.UPDATE_OBJECT) {
        const fields = deps.fields
        const object = createRequestObject(fields)

        let { objectId, datasourceId, tableId } = options

        if (options.fields.length) {
          const { fields } = options
          for (let index in options.fields) {
            if (index) {
              const field = fields[index]

              if (
                field &&
                field.source &&
                field.source.options &&
                field.source.options.operation
              ) {
                delete object[field.fieldId]

                const object1Id = deps.bindingId.value
                const object2Id = deps.fields[field.fieldId].value
                const table1Id = options.bindingId.tableId
                const table2Id = field.source.tableId

                const args = {
                  table1Id,
                  object1Id,
                  table2Id,
                  object2Id,
                  fieldId: field.fieldId,
                  baseURL: getBaseURL(),
                  datasourceId: field.source.datasourceId,
                }

                const { CREATE_ASSOCIATION, DELETE_ASSOCIATION } = actionTypes
                const { operation } = field.source.options

                switch (operation) {
                  case CREATE_ASSOCIATION:
                    await dispatch(createAssociation(args))
                    continue
                  case DELETE_ASSOCIATION:
                    await dispatch(deleteAssociation(args))
                    continue
                }
              }
            }
          }
        }

        if (!objectId) {
          if (!options.bindingId) {
            continue
          }

          objectId = deps.bindingId && deps.bindingId.value
          datasourceId = options.bindingId.datasourceId
          tableId = options.bindingId.tableId
        }

        if (objectId) {
          let datasource = getDatasources()[datasourceId]

          await dispatch(
            updateObject(
              getBaseURL(),
              datasourceId,
              tableId,
              objectId,
              object,
              datasource,
              getParams,
              state
            )
          )
        }
      } else if (actionType === actionTypes.DELETE_OBJECT) {
        if (!options.bindingId) {
          continue
        }

        let { datasourceId, tableId } = options.bindingId
        let objectId = deps.bindingId && deps.bindingId.value
        let datasource = getDatasources()[datasourceId]

        if (objectId) {
          await dispatch(
            deleteObject(getBaseURL(), {
              datasourceId,
              tableId,
              objectId,
              datasource,
              getParams,
              state,
            })
          )
        }
      } else if (
        actionType === actionTypes.CREATE_ASSOCIATION ||
        actionType === actionTypes.DELETE_ASSOCIATION
      ) {
        // Create / Delete association

        let { object1, object2, objectId } = options
        let action = actions[i]
        let { fieldId } = action.options || {}

        if (
          !object1 ||
          !object2 ||
          !fieldId ||
          !deps.object1 ||
          !deps.object2 ||
          !deps.fieldId ||
          object1.datasourceId !== object2.datasourceId
        ) {
          continue
        }

        let authToken = unsafeGetToken(object1.datasourceId)
        let opts = { ...selectorOpts, authToken }

        let object1Id = deps.object1.value || getId(object1, opts) || objectId
        let object2Id = deps.object2.value || getId(object2, opts)
        let table1Id = object1.tableId
        let table2Id = object2.tableId

        if (!object1Id || !object2Id) {
          continue
        }

        let args = {
          table1Id,
          object1Id,
          table2Id,
          object2Id,
          fieldId,
          baseURL: getBaseURL(),
          datasourceId: object1.datasourceId,
        }

        if (actionType === actionTypes.CREATE_ASSOCIATION) {
          // Create
          await dispatch(createAssociation(args))
        } else {
          // Delete
          await dispatch(deleteAssociation(args))
        }
      } else if (actionType === actionTypes.NAVIGATE) {
        let paramValues = {}

        if (deps.params) {
          Object.keys(deps.params).forEach(key => {
            paramValues[key] = deps.params[key] && deps.params[key].value
          })
        }

        let bindings = getBindings && getBindings()

        let app = helpers.getApp()
        bindings = addRelatedBindings(
          { ...bindings, ...createdObjects },
          state,
          app
        )

        let params = {
          ...paramValues,
          ...bindings,
        }

        navigate({ ...options, params })
      } else if (actionType === actionTypes.EXTERNAL_LINK) {
        let { useSystemBrowser } = options
        let { url } = deps

        if (url && url.value) {
          await openLink(url.value, useSystemBrowser)
        }
      } else if (actionType === actionTypes.AUTHENTICATE) {
        let { datasourceId, errorMessage } = options
        let params = { password: deps.password && deps.password.value }
        let app = helpers.getApp()
        if (options.authType === authTypes.USERNAME) {
          params.username = deps.username && deps.username.value
        } else if (options.authType === authTypes.EMAIL) {
          params.email = deps.email && deps.email.value
        }
        try {
          await dispatch(authenticate(getBaseURL(), datasourceId, params))

          // clear fields on success
          const emailId = deps.email && deps.email.inputId
          const usernameId = deps.username && deps.username.inputId
          const passwordId = deps.password && deps.password.inputId
          const ids = [emailId, usernameId, passwordId]
          createInputs = createInputs.concat(inputs, ids)

          registerDevice(
            app,
            helpers.getDeviceId(),
            helpers.getNotificationsURL(),
            unsafeGetToken(datasourceId),
            helpers.isPreviewer
          ).catch(err => console.log('NOTIFICATIONS REGISTRATION ERROR:', err))
        } catch (err) {
          let type = options.authType === authTypes.EMAIL ? 'Email' : 'Username'
          if (errorMessage) {
            Alert.alert(errorMessage)
          } else if (err.response && err.response.data) {
            Alert.alert(err.response.data)
          } else {
            Alert.alert(
              `Invalid ${type} or Password`,
              'Please double-check the information you provided and try again.'
            )
          }

          throw err
        }
      } else if (actionType === actionTypes.SIGNUP) {
        createInputs = createInputs.concat(inputs, getInputIds(deps))
        await executeSignup(dispatch, options, deps, helpers)
      } else if (actionType === actionTypes.LOGOUT) {
        let app = getApp()
        let datasourceIds = Object.keys(app.datasources)

        unregisterDevice(
          app,
          helpers.getDeviceId(),
          helpers.getNotificationsURL(),
          unsafeGetToken(null, app),
          helpers.isPreviewer
        ).catch(err => console.log('NOTIFICATIONS REGISTRATION ERROR:', err))

        dispatch(logout(datasourceIds))
      } else if (actionType === actionTypes.FORGOT_PASSWORD) {
        navigate({
          target: TARGET_FORGOT_PASSWORD,
          transition: transitions.MODAL,
        })
      } else if (
        actionType === actionTypes.AUTHENTICATE_EXTERNAL ||
        actionType === actionTypes.SIGNUP_EXTERNAL
      ) {
        let app = helpers.getApp()
        let datasourceId = Object.keys(app.datasources)[0]

        let authType =
          actionType === actionTypes.AUTHENTICATE_EXTERNAL ? 'login' : 'signup'

        let { customActionId } = options

        const { getBaseURL } = helpers

        try {
          let inputs = {}

          for (let itm in deps) {
            inputs[itm] = deps[itm].value
          }

          let tokenConfig = {
            authToken: app.externalUsers[authType].authToken.formattedKey,
            id: app.externalUsers[authType].id.formattedKey,
          }

          let config = { inputs, tokenConfig, customActionId }
          await dispatch(
            authenticateExternal(getBaseURL(), datasourceId, config)
          )
          registerDevice(
            app,
            helpers.getDeviceId(),
            helpers.getNotificationsURL(),
            unsafeGetToken(datasourceId),
            helpers.isPreviewer
          ).catch(err => console.log('NOTIFICATIONS REGISTRATION ERROR:', err))
        } catch (err) {
          let authTypeFormatted =
            authType.charAt(0).toUpperCase() + authType.slice(1)
          console.error('AUTHENTICATE EXTERNAL ERROR! ', err)
          Alert.alert(`${authTypeFormatted} Failed`)
        }
      } else if (actionType === actionTypes.SHARE) {
        if (options.source && deps.source) {
          await share(options.source, deps.source.value, helpers)
        }
      } else if (actionType === actionTypes.SET_INPUT_VALUE) {
        if (options.inputId) {
          dispatch(
            changeValue(options.inputId, deps.source && deps.source.value)
          )
        }
      } else if (actionType === actionTypes.PUSH_NOTIFICATION) {
        sendNotification(options, deps, helpers).catch(err =>
          console.error('ERROR SENDING NOTIFICATION:', err)
        )
      } else if (actionType === actionTypes.NOTIFICATION_PERMISSION) {
        requestPermission()
      } else if (actionType === actionTypes.CALL_API) {
        await executeCallAPI(dispatch, options, deps, helpers)
      } else if (actionType === actionTypes.AUTHENTICATE_API) {
        // Do auth
        await executeAPILogin(dispatch, options, deps, helpers)
      } else if (actionType === actionTypes.CUSTOM) {
        // CUSTOM_ACTIONS
        const { customActionId } = options
        const outputs = await executeAPICustomAction(
          dispatch,
          options,
          deps,
          helpers
        )

        customActionOutputs[customActionId] = outputs.data.response.data
      } else if (actionType === actionTypes.SIGNUP_API) {
        // Do signup
        await executeAPILogin(dispatch, options, deps, helpers, true)
      } else {
        console.error(`I don't know how to "${actionType}"`)
      }

      let app = getApp()

      logEvent(actionType, app, getComponent(), { ...options }, helpers)
    }

    createInputs.forEach(inputId => {
      if (inputId) {
        dispatch(resetValue(inputId))
      }
    })
  } catch (err) {
    console.warn(err)
  }
}

export const createRequestObject = fields => {
  const mappedObject = mapObject(fields, field => {
    const valueIsNestedDeeper =
      field && !Object.prototype.hasOwnProperty.call(field, 'value')
    if (valueIsNestedDeeper) {
      return createRequestObject(field)
    }

    if (typeof field.value === 'number') {
      return field.value
    }

    return field.value || null
  })
  return toDeep(mappedObject)
}

export const executeCreate = async (
  dispatch,
  options,
  dependencies = {},
  helpers
) => {
  const { getBaseURL, getDatasources, getParams, getStore } = helpers
  const { fields = {} } = dependencies
  const { datasourceId, tableId } = options
  const state = getStore().getState()

  const object = createRequestObject(fields)
  const datasource = getDatasources()[datasourceId]

  const { value } = await dispatch(
    createObject(getBaseURL(), {
      datasourceId,
      tableId,
      object,
      datasource,
      getParams,
      state,
    })
  )

  const obj = value.data

  return obj
}

export const executeSignup = async (
  dispatch,
  options,
  dependencies,
  helpers
) => {
  let { getBaseURL } = helpers
  let { datasourceId, errorMessage } = options

  options = { ...options, tableId: 'users' }
  try {
    await executeCreate(dispatch, options, dependencies, helpers)

    let authCredentials = mapObject(dependencies.fields, dep => dep.value)
    await dispatch(authenticate(getBaseURL(), datasourceId, authCredentials))

    registerDevice(
      helpers.getApp(),
      helpers.getDeviceId(),
      helpers.getNotificationsURL(),
      unsafeGetToken(datasourceId),
      helpers.isPreviewer
    ).catch(err => console.log('NOTIFICATIONS REGISTRATION ERROR:', err))
  } catch (err) {
    Alert.alert(errorMessage || 'User email already exists')

    // Need to throw again so we cancel further actions.
    throw err
  }
}

export const executeCallAPI = async (
  dispatch,
  options,
  dependencies,
  helpers
) => {
  let { params } = prepareAPIDependencies(dependencies.params)
  let { datasourceId, endpoint } = options

  let app = helpers.getApp()
  let datasource = app.datasources[datasourceId]

  if (!datasource) {
    throw new Error(`Invalid datasource: ${datasourceId}`)
  }

  if (!endpoint || !endpoint.collectionId || !endpoint.endpointId) {
    throw new Error('Endpoint info missing')
  }

  let { collectionId, endpointId } = endpoint
  let endpointObj = getEndpoint(datasource, collectionId, endpointId)

  let url = apiURL({ datasource, collectionId, endpointId, apiParams: params })
  let method = getEndpointMethod(endpointObj)

  let response = await request(datasourceId, url, method, params)

  return response
}

const executeAPILogin = async (
  dispatch,
  options,
  dependencies,
  helpers,
  isSignup = false
) => {
  let { datasourceId } = options

  let app = helpers.getApp()
  let datasource = app.datasources[datasourceId]
  let login = isSignup ? datasource.auth.signup : datasource.auth.login

  if (!login) {
    throw new Error('Auth not setup')
  }

  options = {
    ...options,
    endpoint: login.endpoint,
  }

  try {
    let response = await executeCallAPI(
      dispatch,
      options,
      dependencies,
      helpers
    )

    let { fieldId } = login.tokenSource
    let token = response.data[fieldId]

    if (token) {
      dispatch(setAuthToken(datasourceId, token))
    }

    return
  } catch (err) {
    console.warn('AUTH ERROR:', err)
  }

  let errorTitle = 'Invalid Credentials'
  let errorPrompt =
    'Please double-check the information you provided and try again.'

  if (isSignup) {
    errorTitle = 'Error Signup Up'
    errorPrompt = 'You may already have an account with the email address'
  }

  Alert.alert(errorTitle, errorPrompt, [
    { text: 'Try Again', onPress: () => {}, style: 'cancel' },
  ])

  throw new Error('Invalid credentials')
}

export const prepareAPIDependencies = dependencies => {
  let params = {}
  let inputIds = []

  for (let key in dependencies) {
    if (key) {
      params[key] = dependencies[key].value
      inputIds.push(dependencies[key].inputId)
    }
  }

  return { params, inputIds }
}

export const hasLink = action => {
  let actionChain = action.actions || []
  let links = actionChain.filter(a => a.actionType === actionTypes.NAVIGATE)

  return links.length > 0
}
